import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Icon } from "../ui/Icon.tsx";
import { drawMark, INITIAL, PEN_COLORS, render, renderFull, SIZES, type EditState, type Look, type Mark, type Rect } from "./editState.ts";
import { clamp, flip, normalize, rotate } from "./geometry.ts";
import styles from "./ImageEditor.module.css";

type Tool = "crop" | "draw" | "shapes" | "text" | "redact" | "adjust";
type Aspect = "free" | "1:1" | "4:3" | "16:9";
type Point = [number, number];

const TOOLS: { id: Tool; label: string; icon: Parameters<typeof Icon>[0]["name"] }[] = [
  { id: "crop", label: "Zuschneiden", icon: "crop" },
  { id: "draw", label: "Zeichnen", icon: "edit" },
  { id: "shapes", label: "Formen", icon: "rect" },
  { id: "text", label: "Text", icon: "text" },
  { id: "redact", label: "Verbergen", icon: "hide" },
  { id: "adjust", label: "Anpassen", icon: "tune" },
];
const LOOKS: { id: Look; label: string }[] = [
  { id: "none", label: "Original" },
  { id: "mono", label: "S/W" },
  { id: "warm", label: "Warm" },
  { id: "cool", label: "Kalt" },
];
const PREVIEW_EDGE = 1600;
const HANDLE = 14;

export interface EditorResult {
  file: File;
  caption: string;
  asReply: boolean;
}

/**
 * The image editor (like the app's ImageEditorScreen): crop, turn, mirror, draw, shapes, text,
 * hide (pixelate or black), adjust; undo and redo. [send] shows a caption and "Senden" for a
 * picture from the chat; without it "Fertig" hands the picture back (a pending attachment).
 */
export function ImageEditor({ source, send, onDone, onClose }: { source: Blob; send?: { canReply: boolean }; onDone: (result: EditorResult) => Promise<void> | void; onClose: () => void }) {
  const [bitmap, setBitmap] = useState<ImageBitmap>();
  const [failed, setFailed] = useState(false);
  const [state, setState] = useState<EditState>(INITIAL);
  const [past, setPast] = useState<EditState[]>([]);
  const [future, setFuture] = useState<EditState[]>([]);
  const [tool, setTool] = useState<Tool>("draw");
  const [aspect, setAspect] = useState<Aspect>("free");
  const [color, setColor] = useState(PEN_COLORS[2]!);
  const [size, setSize] = useState(1);
  const [highlighter, setHighlighter] = useState(false);
  const [shape, setShape] = useState<"arrow" | "rect" | "ellipse">("arrow");
  const [boxed, setBoxed] = useState(false);
  const [redactMode, setRedactMode] = useState<"pixelate" | "black">("pixelate");
  const [draft, setDraft] = useState<Mark | null>(null);
  const [cropDraft, setCropDraft] = useState<Rect | null>(null);
  const [typing, setTyping] = useState<{ at: Point; text: string } | null>(null);
  const [caption, setCaption] = useState("");
  const [asReply, setAsReply] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [confirmClose, setConfirmClose] = useState(false);
  const display = useRef<HTMLCanvasElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const gesture = useRef<{ start: Point; mode: "new" | "move" | "corner"; corner?: number; crop?: Rect } | null>(null);
  const sliderStart = useRef<EditState | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let gone = false;
    createImageBitmap(source).then(
      (b) => (gone ? b.close() : setBitmap(b)),
      () => setFailed(true),
    );
    return () => {
      gone = true;
    };
  }, [source]);

  useEffect(() => {
    const el = stage.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const changed = past.length > 0;

  function commit(next: EditState, before = state) {
    setPast((p) => [...p.slice(-49), before]);
    setFuture([]);
    setState(next);
  }

  function undo() {
    const prev = past.at(-1);
    if (!prev) return;
    setPast(past.slice(0, -1));
    setFuture([state, ...future]);
    setState(prev);
  }

  function redo() {
    const next = future[0];
    if (!next) return;
    setFuture(future.slice(1));
    setPast([...past, state]);
    setState(next);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (typing || (e.target as HTMLElement).tagName === "INPUT") return;
      if (e.key === "Escape") return void (changed ? setConfirmClose(true) : onClose());
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Everything below the marks (turned, hidden, adjusted), cached while only marks change.
  const redacts = state.marks.filter((m) => m.kind === "redact");
  const baseKey = JSON.stringify([state.rotation, state.flipped, state.brightness, state.contrast, state.saturation, state.look, redacts]);
  const base = useMemo(() => (bitmap ? renderFull(bitmap, { ...state, marks: redacts }, PREVIEW_EDGE, true) : null), [bitmap, baseKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const crop = cropDraft ?? state.crop;
  const view: Rect = tool === "crop" ? { x: 0, y: 0, w: 1, h: 1 } : state.crop;

  // The canvas on screen: the base, the marks, and the crop frame while cropping.
  const fitted = useMemo(() => {
    if (!base || !box.w || !box.h) return null;
    const vw = base.width * view.w;
    const vh = base.height * view.h;
    const k = Math.min(box.w / vw, box.h / vh);
    return { w: Math.max(1, Math.floor(vw * k)), h: Math.max(1, Math.floor(vh * k)) };
  }, [base, box, view.w, view.h]);

  useEffect(() => {
    const canvas = display.current;
    if (!canvas || !base || !fitted) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(fitted.w * dpr);
    canvas.height = Math.round(fitted.h * dpr);
    const full = document.createElement("canvas");
    full.width = base.width;
    full.height = base.height;
    const f = full.getContext("2d")!;
    f.drawImage(base, 0, 0);
    for (const m of state.marks) drawMark(f, m, base.width, base.height);
    if (draft) {
      if (draft.kind === "redact") {
        f.save();
        f.fillStyle = draft.mode === "black" ? "rgb(0 0 0 / 0.7)" : "rgb(255 255 255 / 0.35)";
        f.strokeStyle = "#fff";
        f.lineWidth = 2;
        f.setLineDash([8, 6]);
        const r = draft.rect;
        f.fillRect(r.x * base.width, r.y * base.height, r.w * base.width, r.h * base.height);
        f.strokeRect(r.x * base.width, r.y * base.height, r.w * base.width, r.h * base.height);
        f.restore();
      } else drawMark(f, draft, base.width, base.height);
    }
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(full, view.x * base.width, view.y * base.height, view.w * base.width, view.h * base.height, 0, 0, canvas.width, canvas.height);
    if (tool === "crop") {
      const x = crop.x * canvas.width;
      const y = crop.y * canvas.height;
      const w = crop.w * canvas.width;
      const h = crop.h * canvas.height;
      ctx.save();
      ctx.fillStyle = "rgb(0 0 0 / 0.55)";
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.rect(x, y, w, h);
      ctx.fill("evenodd");
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2 * dpr;
      ctx.strokeRect(x, y, w, h);
      ctx.lineWidth = 1 * dpr;
      ctx.globalAlpha = 0.5;
      for (const t of [1 / 3, 2 / 3]) {
        ctx.beginPath();
        ctx.moveTo(x + w * t, y);
        ctx.lineTo(x + w * t, y + h);
        ctx.moveTo(x, y + h * t);
        ctx.lineTo(x + w, y + h * t);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      const s = HANDLE * dpr * 0.6;
      ctx.fillStyle = "#fff";
      for (const [cx, cy] of [[x, y], [x + w, y], [x, y + h], [x + w, y + h]] as Point[]) ctx.fillRect(cx - s / 2, cy - s / 2, s, s);
      ctx.restore();
    }
  }, [base, fitted, state.marks, draft, tool, crop, view.x, view.y, view.w, view.h]);

  /** A pointer position in the picture's coordinates (0..1 of the turned picture). */
  function point(e: { clientX: number; clientY: number }): Point {
    const r = display.current!.getBoundingClientRect();
    const u = (e.clientX - r.left) / r.width;
    const v = (e.clientY - r.top) / r.height;
    return [clamp(view.x + u * view.w), clamp(view.y + v * view.h)];
  }

  /** Normalized height for a normalized width at the chosen aspect (in pixels of the picture). */
  function aspectHeight(w: number): number | null {
    if (aspect === "free" || !base) return null;
    const [a, b] = aspect.split(":").map(Number) as [number, number];
    return (w * base.width * b) / (a * base.height);
  }

  function fitAspect(a: Aspect, s = state) {
    setAspect(a);
    if (a === "free" || !base) return;
    const [p, q] = a.split(":").map(Number) as [number, number];
    const c = s.crop;
    let w = c.w;
    let h = (w * base.width * q) / (p * base.height);
    if (h > c.h) {
      h = c.h;
      w = (h * base.height * p) / (q * base.width);
    }
    commit({ ...s, crop: { x: c.x + (c.w - w) / 2, y: c.y + (c.h - h) / 2, w, h } }, s);
  }

  function onPointerDown(e: ReactPointerEvent<HTMLCanvasElement>) {
    if (!base || typing) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = point(e);
    const markSize = SIZES[size]!;
    if (tool === "crop") {
      const r = display.current!.getBoundingClientRect();
      const near = (cx: number, cy: number) => Math.abs((e.clientX - r.left) / r.width - cx) * r.width < HANDLE && Math.abs((e.clientY - r.top) / r.height - cy) * r.height < HANDLE;
      const c = state.crop;
      const corners: Point[] = [[c.x, c.y], [c.x + c.w, c.y], [c.x, c.y + c.h], [c.x + c.w, c.y + c.h]];
      const corner = corners.findIndex(([cx, cy]) => near(cx, cy));
      const inside = p[0] > c.x && p[0] < c.x + c.w && p[1] > c.y && p[1] < c.y + c.h;
      gesture.current = { start: p, mode: corner >= 0 ? "corner" : inside ? "move" : "new", corner, crop: c };
    } else if (tool === "draw") {
      gesture.current = { start: p, mode: "new" };
      setDraft({ kind: "stroke", color, size: markSize, highlighter, points: [p] });
    } else if (tool === "shapes") {
      gesture.current = { start: p, mode: "new" };
      setDraft({ kind: "shape", shape, color, size: markSize, from: p, to: p });
    } else if (tool === "redact") {
      gesture.current = { start: p, mode: "new" };
      setDraft({ kind: "redact", mode: redactMode, rect: { x: p[0], y: p[1], w: 0, h: 0 } });
    } else if (tool === "text") {
      // The field opens on release: mousedown would take the focus from it at once.
      gesture.current = { start: p, mode: "new" };
    }
  }

  function onPointerMove(e: ReactPointerEvent<HTMLCanvasElement>) {
    const g = gesture.current;
    if (!g) return;
    const p = point(e);
    if (tool === "crop") {
      const c = g.crop!;
      if (g.mode === "move") {
        const dx = clamp(p[0] - g.start[0], -c.x, 1 - c.x - c.w);
        const dy = clamp(p[1] - g.start[1], -c.y, 1 - c.y - c.h);
        setCropDraft({ ...c, x: c.x + dx, y: c.y + dy });
        return;
      }
      // The fixed corner is the opposite one (or the start for a new frame).
      const anchor: Point = g.mode === "new" ? g.start : [g.corner! % 2 === 0 ? c.x + c.w : c.x, g.corner! < 2 ? c.y + c.h : c.y];
      let r = normalize(anchor, p);
      const h = aspectHeight(r.w);
      if (h != null) {
        const fitted = Math.min(h, p[1] < anchor[1] ? anchor[1] : 1 - anchor[1]);
        const w = fitted === h ? r.w : (r.w * fitted) / h;
        r = { x: p[0] < anchor[0] ? anchor[0] - w : anchor[0], y: p[1] < anchor[1] ? anchor[1] - fitted : anchor[1], w, h: fitted };
      }
      setCropDraft(r);
    } else if (draft?.kind === "stroke") {
      setDraft({ ...draft, points: [...draft.points, p] });
    } else if (draft?.kind === "shape") {
      setDraft({ ...draft, to: p });
    } else if (draft?.kind === "redact") {
      setDraft({ ...draft, rect: normalize(g.start, p) });
    }
  }

  function onPointerUp() {
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    if (tool === "text") return setTyping({ at: g.start, text: "" });
    if (tool === "crop") {
      if (cropDraft && cropDraft.w > 0.02 && cropDraft.h > 0.02) commit({ ...state, crop: cropDraft });
      setCropDraft(null);
      return;
    }
    if (draft && !(draft.kind === "redact" && (draft.rect.w < 0.005 || draft.rect.h < 0.005))) commit({ ...state, marks: [...state.marks, draft] });
    setDraft(null);
  }

  function placeText() {
    if (!typing) return;
    if (typing.text.trim()) commit({ ...state, marks: [...state.marks, { kind: "label", text: typing.text.trim(), color, size: SIZES[size]!, at: typing.at, boxed }] });
    setTyping(null);
  }

  function slide(field: "brightness" | "contrast" | "saturation", value: number) {
    sliderStart.current ??= state;
    setState({ ...state, [field]: value });
  }

  function slideEnd() {
    const before = sliderStart.current;
    sliderStart.current = null;
    if (before && before !== state) commit(state, before);
  }

  async function finish() {
    if (!bitmap || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const canvas = render(bitmap, state);
      const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/jpeg", 0.85));
      if (!blob) throw new Error("no blob");
      await onDone({ file: new File([blob], "bild.jpg", { type: "image/jpeg" }), caption, asReply: Boolean(send?.canReply && asReply) });
    } catch {
      setError(send ? "Nicht gesendet. Bitte noch einmal versuchen." : "Das Bild konnte nicht erstellt werden.");
      setBusy(false);
    }
  }

  const screen = typing && display.current ? display.current.getBoundingClientRect() : null;

  return (
    <div className={styles.editor} role="dialog" aria-label="Bild bearbeiten">
      <div className={styles.top}>
        <button className={styles.iconButton} onClick={() => (changed ? setConfirmClose(true) : onClose())} aria-label="Schließen">
          <Icon name="close" />
        </button>
        <div className={styles.grow} />
        <button className={styles.iconButton} onClick={undo} disabled={!past.length} aria-label="Rückgängig" title="Rückgängig (Strg/⌘ + Z)">
          <Icon name="undo" />
        </button>
        <button className={styles.iconButton} onClick={redo} disabled={!future.length} aria-label="Wiederholen" title="Wiederholen (Strg/⌘ + Shift + Z)">
          <Icon name="redo" />
        </button>
        {!send && (
          <button className={styles.primary} onClick={() => void finish()} disabled={!bitmap || busy}>
            Fertig
          </button>
        )}
      </div>

      <div className={styles.stage} ref={stage}>
        {failed && <p className={styles.note}>Das Bild ließ sich nicht öffnen.</p>}
        {fitted && (
          <canvas
            ref={display}
            className={styles[`canvas_${tool}`]}
            style={{ width: fitted.w, height: fitted.h }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            aria-label="Bild"
          />
        )}
        {typing && screen && (
          <input
            className={styles.typing}
            style={{
              left: screen.left + ((typing.at[0] - view.x) / view.w) * screen.width,
              top: screen.top + ((typing.at[1] - view.y) / view.h) * screen.height,
              color,
            }}
            autoFocus
            value={typing.text}
            placeholder="Text"
            onChange={(e) => setTyping({ ...typing, text: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") placeText();
              if (e.key === "Escape") setTyping(null);
            }}
            onBlur={placeText}
            aria-label="Text"
          />
        )}
      </div>

      <div className={styles.options}>
        {tool === "crop" && (
          <>
            {(["free", "1:1", "4:3", "16:9"] as Aspect[]).map((a) => (
              <button key={a} className={aspect === a ? styles.chipOn : styles.chip} onClick={() => fitAspect(a)}>
                {a === "free" ? "Frei" : a}
              </button>
            ))}
            <span className={styles.divider} />
            <button className={styles.chip} onClick={() => commit(rotate(state))}>
              <Icon name="rotate" size={16} /> Drehen
            </button>
            <button className={styles.chip} onClick={() => commit(flip(state))}>
              <Icon name="flip" size={16} /> Spiegeln
            </button>
          </>
        )}
        {(tool === "draw" || tool === "shapes" || tool === "text") && (
          <>
            {tool === "shapes" &&
              (
                [
                  ["arrow", "Pfeil", "arrow"],
                  ["rect", "Rechteck", "rect"],
                  ["ellipse", "Ellipse", "circle"],
                ] as const
              ).map(([id, label, icon]) => (
                <button key={id} className={shape === id ? styles.chipOn : styles.chip} onClick={() => setShape(id)}>
                  <Icon name={icon} size={16} /> {label}
                </button>
              ))}
            {tool === "text" && (
              <>
                <button className={!boxed ? styles.chipOn : styles.chip} onClick={() => setBoxed(false)}>
                  Schlicht
                </button>
                <button className={boxed ? styles.chipOn : styles.chip} onClick={() => setBoxed(true)}>
                  Kasten
                </button>
              </>
            )}
            {tool !== "draw" && <span className={styles.divider} />}
            {PEN_COLORS.map((c) => (
              <button key={c} className={color === c ? styles.swatchOn : styles.swatch} style={{ background: c }} onClick={() => setColor(c)} aria-label={`Farbe ${c}`} />
            ))}
            <span className={styles.divider} />
            {SIZES.map((_, i) => (
              <button key={i} className={size === i ? styles.sizeOn : styles.size} onClick={() => setSize(i)} aria-label={["Dünn", "Mittel", "Dick"][i]}>
                <span style={{ width: 4 + i * 5, height: 4 + i * 5 }} />
              </button>
            ))}
            {tool === "draw" && (
              <>
                <span className={styles.divider} />
                <button className={highlighter ? styles.chipOn : styles.chip} onClick={() => setHighlighter(!highlighter)}>
                  Marker
                </button>
              </>
            )}
          </>
        )}
        {tool === "redact" && (
          <>
            <button className={redactMode === "pixelate" ? styles.chipOn : styles.chip} onClick={() => setRedactMode("pixelate")}>
              Verpixeln
            </button>
            <button className={redactMode === "black" ? styles.chipOn : styles.chip} onClick={() => setRedactMode("black")}>
              Schwärzen
            </button>
            <span className={styles.hint}>{redactMode === "black" ? "Bereich aufziehen, er wird schwarz." : "Bereich aufziehen. Schwärzen ist sicherer als Verpixeln, wenn Text unlesbar werden muss."}</span>
          </>
        )}
        {tool === "adjust" && (
          <>
            {(
              [
                ["brightness", "Helligkeit", 0.5],
                ["contrast", "Kontrast", 0.5],
                ["saturation", "Sättigung", 1],
              ] as const
            ).map(([field, label, range]) => (
              <label key={field} className={styles.slider}>
                <span>{label}</span>
                <input
                  type="range"
                  min={-range}
                  max={range}
                  step={range / 50}
                  value={state[field]}
                  onChange={(e) => slide(field, Number(e.target.value))}
                  onPointerUp={slideEnd}
                  onKeyUp={slideEnd}
                  onDoubleClick={() => commit({ ...state, [field]: 0 })}
                />
              </label>
            ))}
            <span className={styles.divider} />
            {LOOKS.map((l) => (
              <button key={l.id} className={state.look === l.id ? styles.chipOn : styles.chip} onClick={() => commit({ ...state, look: l.id })}>
                {l.label}
              </button>
            ))}
          </>
        )}
        {tool === "text" && <span className={styles.hint}>Ins Bild klicken, um Text zu setzen.</span>}
      </div>

      <div className={styles.tools} role="tablist">
        {TOOLS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tool === t.id} className={tool === t.id ? styles.toolOn : styles.tool} onClick={() => setTool(t.id)}>
            <Icon name={t.icon} size={20} />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {send && (
        <div className={styles.send}>
          <input className={styles.caption} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Bildunterschrift" aria-label="Bildunterschrift" onKeyDown={(e) => e.key === "Enter" && void finish()} />
          {send.canReply && (
            <label className={styles.reply}>
              <input type="checkbox" checked={asReply} onChange={(e) => setAsReply(e.target.checked)} /> Als Antwort auf das Original
            </label>
          )}
          <button className={styles.primary} onClick={() => void finish()} disabled={!bitmap || busy}>
            <Icon name="send" size={18} /> Senden
          </button>
        </div>
      )}
      {error && <p className={styles.error}>{error}</p>}

      {confirmClose && (
        <div className={styles.confirm} role="alertdialog" aria-label="Änderungen verwerfen?">
          <div>
            <b>Änderungen verwerfen?</b>
            <div className={styles.confirmRow}>
              <button className={styles.textButton} onClick={() => setConfirmClose(false)}>
                Weiter bearbeiten
              </button>
              <button className={styles.primary} onClick={onClose}>
                Verwerfen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
